// Pro Trade strategy engine — timeframe-bucketed Forex signal logic.
// Pure functions: given candles [{time,open,high,low,close}] + timeframe seconds,
// return { side:"CALL"|"PUT", confidence:0-100, strategy, reason } or null.

const emaArr = (vals, p) => { const k = 2 / (p + 1); const out = []; let prev = vals[0]; vals.forEach((v, i) => { prev = i ? v * k + prev * (1 - k) : v; out.push(prev); }); return out; };
const smaArr = (vals, p) => vals.map((_, i) => (i < p - 1 ? null : vals.slice(i - p + 1, i + 1).reduce((a, b) => a + b, 0) / p));
const rsiArr = (vals, p = 14) => {
  const out = vals.map(() => null); if (vals.length <= p) return out;
  let g = 0, l = 0;
  for (let i = 1; i <= p; i++) { const d = vals[i] - vals[i - 1]; if (d >= 0) g += d; else l -= d; }
  g /= p; l /= p; out[p] = l === 0 ? 100 : 100 - 100 / (1 + g / l);
  for (let i = p + 1; i < vals.length; i++) { const d = vals[i] - vals[i - 1]; const gg = d > 0 ? d : 0; const ll = d < 0 ? -d : 0; g = (g * (p - 1) + gg) / p; l = (l * (p - 1) + ll) / p; out[i] = l === 0 ? 100 : 100 - 100 / (1 + g / l); }
  return out;
};
const macd = (vals) => { const ef = emaArr(vals, 12); const es = emaArr(vals, 26); const line = vals.map((_, i) => ef[i] - es[i]); const signal = emaArr(line, 9); return { line, signal }; };
const swing = (c, look = 20) => { const r = c.slice(-look); return { res: Math.max(...r.map((x) => x.high)), sup: Math.min(...r.map((x) => x.low)) }; };
const isBull = (k) => k.close > k.open;
const engulf = (prev, cur) => {
  if (cur.close > cur.open && prev.close < prev.open && cur.close >= prev.open && cur.open <= prev.close) return "bull";
  if (cur.close < cur.open && prev.close > prev.open && cur.close <= prev.open && cur.open >= prev.close) return "bear";
  return null;
};

export const tfBucketName = (tfSec) => (tfSec <= 30 ? "Scalping" : tfSec <= 300 ? "Momentum" : "Trend Following");

export function evaluate(candles, tfSec) {
  if (!candles || candles.length < 60) return null;
  const cl = candles.map((c) => c.close);
  const i = cl.length - 1;
  const last = candles[i], prev = candles[i - 1];
  const r = rsiArr(cl, 14)[i];
  const e9 = emaArr(cl, 9), e21 = emaArr(cl, 21), e50 = emaArr(cl, 50), e200 = emaArr(cl, 200);
  const m = macd(cl);
  const { res, sup } = swing(candles, 20);
  if (r == null) return null;
  let side = null, conf = 0, strategy, reason = [];

  if (tfSec <= 30) {
    strategy = "Scalping";
    const up = e9[i] > e21[i], down = e9[i] < e21[i];
    const eng = engulf(prev, last);
    if (up && r > 50 && r < 72 && isBull(last)) {
      side = "CALL"; conf = 55; reason.push("EMA9>EMA21 uptrend", `RSI ${r.toFixed(0)} bullish`, "bullish candle");
      if (eng === "bull") { conf += 18; reason.push("bullish engulfing"); }
    } else if (down && r < 50 && r > 28 && !isBull(last)) {
      side = "PUT"; conf = 55; reason.push("EMA9<EMA21 downtrend", `RSI ${r.toFixed(0)} bearish`, "bearish candle");
      if (eng === "bear") { conf += 18; reason.push("bearish engulfing"); }
    }
  } else if (tfSec <= 300) {
    strategy = "EMA Cross + RSI/MACD";
    const crossUp = e9[i] > e21[i] && e9[i - 1] <= e21[i - 1];
    const crossDn = e9[i] < e21[i] && e9[i - 1] >= e21[i - 1];
    const trendUp = e9[i] > e21[i] && cl[i] > e50[i];
    const trendDn = e9[i] < e21[i] && cl[i] < e50[i];
    const macdUp = m.line[i] > m.signal[i];
    const range = res - sup || 1;
    if (crossUp || trendUp) {
      side = "CALL"; conf = 38; reason.push(crossUp ? "EMA9 crossed above EMA21" : "Price>EMA50 & EMA9>EMA21");
      if (r > 50) { conf += 15; reason.push("RSI>50"); }
      if (macdUp) { conf += 15; reason.push("MACD bullish"); }
      if ((cl[i] - sup) / range < 0.4) { conf += 10; reason.push("bouncing off support"); }
      if (crossUp) conf += 10;
    } else if (crossDn || trendDn) {
      side = "PUT"; conf = 38; reason.push(crossDn ? "EMA9 crossed below EMA21" : "Price<EMA50 & EMA9<EMA21");
      if (r < 50) { conf += 15; reason.push("RSI<50"); }
      if (!macdUp) { conf += 15; reason.push("MACD bearish"); }
      if ((res - cl[i]) / range < 0.4) { conf += 10; reason.push("rejecting resistance"); }
      if (crossDn) conf += 10;
    }
  } else {
    strategy = "Trend Following";
    const up = e50[i] > e200[i] && cl[i] > e50[i];
    const dn = e50[i] < e200[i] && cl[i] < e50[i];
    const macdUp = m.line[i] > m.signal[i];
    if (up) {
      side = "CALL"; conf = 50; reason.push("EMA50>EMA200 uptrend", "price above EMA50");
      if (macdUp) { conf += 20; reason.push("MACD bullish"); }
      if (r > 50 && r < 75) { conf += 12; reason.push("RSI confirms"); }
    } else if (dn) {
      side = "PUT"; conf = 50; reason.push("EMA50<EMA200 downtrend", "price below EMA50");
      if (!macdUp) { conf += 20; reason.push("MACD bearish"); }
      if (r < 50 && r > 25) { conf += 12; reason.push("RSI confirms"); }
    }
  }
  if (!side) return null;
  return { side, confidence: Math.min(95, Math.round(conf)), strategy, reason: reason.join(" · ") };
}
